import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrganizationUnitClient, OrganizationUnitDto, PersonDto } from '../generated/api-client';

export type OrganizationUnitProfileDto = OrganizationUnitDto & {
  regionalChurchId?: string;
  regionalChurchName?: string;
};

export {
  OrganizationUnitDto,
  OrganizationUnitDetailDto,
  OrganizationUnitTreeNodeDto,
  OrganizationUnitByLevelDto,
  PaginatedResponseOfOrganizationUnitDto,
  MinistryPersonGroupDto,
  PersonDto,
  UpdateUnitRequest,
  OrganizationUnitScheduleDto,
  CreateScheduleRequest,
} from '../generated/api-client';

/**
 * Thin wrapper over the NSwag-generated OrganizationUnitClient.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationUnitApiService {
  constructor(private readonly client: OrganizationUnitClient) {}

  /** Returns all units (flat list, no pagination) */
  getAllWithoutPagination() {
    return this.client.getAllWithoutPagination();
  }

  /** Paginated list of all units */
  getAll(
    searchTerm?: string | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    page?: number,
    pageSize?: number
  ) {
    return this.client.getAll(searchTerm, sortBy, sortDirection, page, pageSize);
  }

  /** Paginated list of communities (leaf-level units) */
  getCommunities(
    searchTerm?: string | null,
    sortBy?: string | null,
    sortDirection?: string | null,
    page?: number,
    pageSize?: number,
    areaId?: string | null,
    districtId?: string | null,
    groupId?: string | null
  ) {
    return this.client.getCommunities(searchTerm, sortBy, sortDirection, page, pageSize, areaId, districtId, groupId);
  }

  /** Autocomplete-optimised search across all units */
  search(searchTerm?: string | null, page?: number, pageSize?: number, levelId?: number | null, maxLevelId?: number | null) {
    return (this.client as any).search(searchTerm, page, pageSize, levelId, maxLevelId);
  }

  getById(id: string) {
    return this.client.getById(id);
  }

  update(id: string, request: import('../generated/api-client').UpdateUnitRequest) {
    return this.client.update(id, request);
  }

  getHigherLevelUnits(minExcludedLevelId?: number | null) {
    return this.client.getHigherLevelUnits(minExcludedLevelId);
  }

  /** Ministry holders grouped by ministry for a given unit */
  getMinistriesByUnit(id: string) {
    return this.client.getMinistriesByUnit(id);
  }

  /** Flat list of all members belonging to a unit */
  getMembersByUnit(id: string, includeDeparted = false): Observable<PersonDto[]> {
    return (this.client as any).getMembersByUnit(id, includeDeparted);
  }

  /** Full descendant tree of a unit (for tree-view components) */
  getDescendantTree(id: string) {
    return this.client.getDescendantTree(id);
  }

  deleteUnit(id: string, reason?: string) {
    return this.client.delete(id, reason ?? null);
  }

  create(request: CreateUnitRequest) {
    return (this.client as any).create(request);
  }

  /** Returns the active divine-service schedules for a unit */
  getSchedules(unitId: string) {
    return this.client.getSchedules(unitId);
  }

  /** Creates a new schedule for a community unit */
  createSchedule(unitId: string, request: import('../generated/api-client').CreateScheduleRequest) {
    return this.client.createSchedule(unitId, request);
  }

  /** Soft-deletes a schedule from a unit */
  deleteSchedule(unitId: string, scheduleId: number) {
    return this.client.deleteSchedule(unitId, scheduleId);
  }

  /** Changes the parent unit (correction or structural change) */
  changeParent(unitId: string, request: ChangeParentRequest) {
    return (this.client as any).changeParent(unitId, request);
  }
}

/** Matches the CreateUnitRequest schema generated by NSwag from POST /api/organizationunit */
export interface CreateUnitRequest {
  name: string;
  shortName: string;
  costCentre?: string | null;
  description?: string | null;
  organizationLevelId: number;
  parentUnitId?: string | null;
}

/** Matches the ChangeParentRequest schema from PUT /api/organizationunit/{id}/parent */
export interface ChangeParentRequest {
  newParentUnitId: string;
  isCorrection: boolean;
  reason?: string | null;
  effectiveDate?: string | null; // ISO date string (YYYY-MM-DD), required when isCorrection=false
}
